// Turns an in-progress editor map into a paste-ready TS snippet matching
// the existing hand-authored skirmish-maps.ts convention (an ASCII grid +
// legend fed through parseMap, plus the overrides/playerSlots object
// literals) — reusable as a starting point for a new skirmish-maps.ts entry
// or a campaign .mission.ts file (a dev adds initialUnits/players/
// winCondition by hand for the latter). Deliberately hardcoded here rather
// than imported from wars-engine — wars-net doesn't depend on wars-engine
// (see TerrainMapPreview's own comment), it only ever talks to the server
// over HTTP.
const TERRAIN_LEGEND: { [terrainType: string]: string } = {
  forest: 'f',
  mountain: 'm',
  hill: 'h',
  road: 'r',
  river: 'w',
  sea: 's',
  beach: 'b',
  dock: 'D',
  fort: 'F',
  barracks: 'B',
  workshop: 'W',
  village: 'V',
  farm: 'A',
};

function slugify(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  return slug || 'custom-map';
}

function toCamelCase(slug: string): string {
  return slug.replace(/-([a-z0-9])/g, (_, c) => c.toUpperCase());
}

export interface ExportableMap {
  name: string;
  description: string;
  width: number;
  height: number;
  terrainData: { [key: string]: string };
  ownerOverrides: { [key: string]: number };
  definitionOverrides: { [key: string]: string };
  initialUnits: { [key: string]: { type: string; playerId: number } };
  playerSlots: { startingFunds: number }[];
}

export function generateMapCode(input: ExportableMap): string {
  const usedTypes = new Set(Object.values(input.terrainData));
  const legendEntries = Object.entries(TERRAIN_LEGEND).filter(([type]) =>
    usedTypes.has(type)
  );

  const rows: string[] = [];
  for (let y = 0; y < input.height; y++) {
    let row = '';
    for (let x = 0; x < input.width; x++) {
      const type = input.terrainData[`${x},${y}`] ?? 'grass';
      row += type === 'grass' ? '.' : TERRAIN_LEGEND[type] ?? '.';
    }
    rows.push(row);
  }

  const slug = slugify(input.name);
  const varName = toCamelCase(slug);
  const legendLiteral = legendEntries
    .map(([type, char]) => `${char}: '${type}'`)
    .join(', ');
  const rowsLiteral = rows.map((row) => `    '${row}',`).join('\n');

  const unitEntries = Object.entries(input.initialUnits);
  const unitsLiteral = unitEntries
    .map(([key, entry], i) => {
      const [x, y] = key.split(',').map(Number);
      return `    { id: 'custom-${i}', type: '${entry.type}', playerId: ${entry.playerId}, x: ${x}, y: ${y}, health: 10, canMove: false, hasMoved: false },`;
    })
    .join('\n');

  return `const ${varName}Terrain = parseMap(
  [
${rowsLiteral}
  ],
  { ${legendLiteral} },
);

export const ${varName}Map: MapDefinition = {
  id: '${slug}',
  name: '${input.name.replace(/'/g, "\\'")}',
  description: '${input.description.replace(/'/g, "\\'")}',
  terrainMap: ${varName}Terrain,
  ownerOverrides: ${JSON.stringify(input.ownerOverrides)},
  definitionOverrides: ${JSON.stringify(input.definitionOverrides)},
  initialUnits: [
${unitsLiteral}
  ],
  playerSlots: ${JSON.stringify(input.playerSlots)},
};
`;
}
